// Libs
import classNames from 'classnames/bind';
import { useTranslation } from 'react-i18next';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid2, Stack } from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useState } from 'react';
// Components, Layouts, Pages
import { BaseInput } from '~/components/common';
// Others
import { useAppDispatch } from '~/utils/redux/hooks';
import { IFormChangePassword } from '~/utils/interface/user';
import { defaultValues, schema } from './helper';
import { changePassword } from '~/thunks/user/userThunk';
// Styles, images, icons
import styles from './FormChangePassword.module.scss';

type Props = {
  onClose: () => void;
};

const cx = classNames.bind(styles);

const ModalFormTaskJob = (props: Props) => {
  //#region Destructuring Props
  const { onClose } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/profile', 'common/base']);
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<IFormChangePassword>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValues,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleCancelFormChangePassword = () => {
    reset(defaultValues);
    onClose();
  };

  const handleSubmitForm = (data: IFormChangePassword) => {
    setIsLoading(true);
    dispatch(changePassword({ currentPassword: data.currentPassword, newPassword: data.newPassword }))
      .unwrap()
      .then(() => {
        handleCancelFormChangePassword();
      })
      .catch(() => {})
      .finally(() => {
        setIsLoading(false);
      });
  };
  //#endregion Handle Function

  return (
    <Dialog
      id='modalFormChangePassword'
      open={true}
      fullWidth
      maxWidth={'sm'}
      onClose={handleCancelFormChangePassword}
    >
      <Box component={'form'} className={cx('form')} onSubmit={handleSubmit(handleSubmitForm)}>
        <DialogTitle>{t('form_change_password_title')}</DialogTitle>

        <DialogContent dividers={true}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 container size={12} columnSpacing={'12px'}>
              <Grid2 size={12}>
                <Controller
                  name='currentPassword'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_change_password_current_password_label')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.currentPassword?.message}
                      required
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={12}>
                <Controller
                  name='newPassword'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_change_password_new_password_label')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.newPassword?.message}
                      required
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={12}>
                <Controller
                  name='confirmNewPassword'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      label={t('form_change_password_confirm_password_label')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.confirmNewPassword?.message}
                      required
                    />
                  )}
                />
              </Grid2>
            </Grid2>
          </Grid2>
        </DialogContent>

        <DialogActions>
          <Stack
            direction={'row'}
            alignItems={'center'}
            justifyContent={'flex-end'}
            component={'div'}
            spacing={'12px'}
            className={cx('formFooter')}
          >
            <Button variant='outlined' onClick={handleCancelFormChangePassword}>
              {t('common_action_cancel', { ns: 'common/base' })}
            </Button>

            <Button variant='contained' type='submit' disabled={isLoading}>
              {t('common_action_save', { ns: 'common/base' })}
            </Button>
          </Stack>
        </DialogActions>
      </Box>
    </Dialog>
  );
};

export default ModalFormTaskJob;
